<template>
  <header class="header">
    <Wrap class="wrap">
      <div class="inner">
        <SkeletonItem
          v-if="loading"
          class="breadcrumbs-skeleton"
          is-animated
        />
        <Breadcrumbs
          v-else-if="crumbs.length"
          :breadcrumbs="crumbs"
          :events-identifier="eventsIdentifier"
          :track-event="breadcrumbsTrackEvent"
        />
        <div class="content">
          <SkeletonItem
            v-if="loading"
            is-animated
            class="title title_skeleton"
          />
          <h1
            class="title"
            v-html="title"
          />
          <div
            v-if="subtitle"
            class="extra-header"
            :class="{ expanded, overflow: heightOverflow }"
          >
            <div
              ref="text"
              v-html="subtitle"
            />
            <div
              v-if="heightOverflow"
              class="extra-header__excerpt"
            >
              <span @click="toggleExpanded">{{ t('moreButtonTitle') }}</span>
            </div>
          </div>
        </div>
      </div>
    </Wrap>
  </header>
</template>

<script lang="ts" setup>
  import { Breadcrumbs, type Breadcrumb, type TrackEvent } from '@experience/core-breadcrumbs-ui';
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { useI18n } from '@experience/i18n';
  import { computed, onMounted, onUnmounted, useTemplateRef, ref } from 'vue';

  import { trackEvent } from '@/services/analytics';
  import { ru } from '@/pages/regionArticles/features/articlesHeader/i18n/ru';
  import type { GeoItem } from '@/pages/regionArticles/features/articlesHeader/models/geoItem';
  import Wrap from '@/shared/ui/Wrap.vue';
  import { useScreenSize } from '@experience/shared';

  interface Props {
    /**
     * Текст заголовка.
     */
    title: string;

    /**
     * Текст подзаголовка.
     */
    subtitle?: string;

    /**
     * Страна.
     */
    country?: GeoItem;

    /**
     * Город.
     */
    city?: GeoItem;

    /**
     * Идентификато аналитических событий.
     */
    eventsIdentifier: string;

    /**
     * Флаг загрузки данных.
     */
    loading: boolean;
  }

  const props = defineProps<Props>();

  const breadcrumbsTrackEvent = trackEvent as TrackEvent;

  const { t } = useI18n({ inheritLocale: true, messages: { ru } });
  const { isDesktop } = useScreenSize();

  const textRef = useTemplateRef('text');

  const mounted = ref(false);

  const heightOverflow = computed(() =>
    !mounted.value ? false : textRef.value !== null && textRef.value?.offsetHeight > (!isDesktop.value ? 166 : 192),
  );

  const crumbs = computed(() => {
    const items: Breadcrumb[] = [{ label: t('journal'), to: '/journal' }];

    if (props.country) {
      items.push({ label: props.country.name, to: `/journal/country/articles/${props.country.slug}/` });
    }

    items.push({ label: props.title, to: '', schemaOnly: true });

    return items;
  });

  const expanded = ref(false);

  const toggleExpanded = () => {
    if (heightOverflow.value) {
      expanded.value = !expanded.value;
    }
  };

  onMounted(() => {
    mounted.value = true;
  });

  onUnmounted(() => {
    mounted.value = false;
  });
</script>

<style lang="scss" scoped>
  // TODO: перенести на токены, когда они будут готовы
  .header {
    position: relative;
    box-sizing: content-box;

    .wrap {
      height: 100%;
    }
  }

  .inner {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    height: 100%;
    padding: 24px 0 8px;

    @include tablet {
      padding: 24px 0 0;
    }
  }

  .breadcrumbs-skeleton {
    width: 260px;
    height: 34px;
    margin: 7px 1px; // padding (6px 0) + border (1px)

    @include tablet {
      width: 100px;
    }
  }

  .content {
    position: relative;
    z-index: 3;
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
    max-width: 909px;
    margin: auto 0 8px;

    @include tablet {
      margin-top: 0;
    }
  }

  .title {
    margin: 32px 0 0;
    font-weight: 800;
    font-size: 40px;
    line-height: 48px;
    text-align: left;
    word-break: break-word;

    &_skeleton {
      width: 450px;
      height: 40px;

      @include mobile {
        width: 260px;
        height: 32px;
      }
    }

    @include tablet {
      font-size: 32px;
      line-height: 40px;
    }
  }

  .extra-header {
    position: relative;
    overflow: hidden;
    max-height: 192px;
    margin: 0;
    font-weight: 400;
    font-size: 17px;
    line-height: 24px;
    text-align: left;

    &.expanded {
      max-height: 100%;
    }

    @include tablet {
      max-height: 166px;
      margin: 0;
    }
  }

  .extra-header__excerpt {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    display: none;
    padding: 0 11px 0 45px;
    background: linear-gradient(
      90deg,
      transparent,
      var(--alias-color-background-default) 37%,
      var(--alias-color-background-default) 99%
    );
    color: var(--alias-color-link-default);
    font-size: 17px;
    line-height: 24px;
    cursor: pointer;

    &:hover {
      color: var(--alias-color-link-default-hovered);
    }

    .overflow,
    .overflow:not(.expanded) & {
      display: block;
    }
  }
</style>
